/**
 * Date/time pure utilities for the calendar module. `event-layout.ts` re-points
 * here for parseEventDate/isDateOnlyString.
 */
import { DATE_ONLY_RE } from './calendarConstants';

/**
 * Check if a value is a date-only string (YYYY-MM-DD)
 */
export function isDateOnlyString(value: unknown): value is string {
  return typeof value === 'string' && DATE_ONLY_RE.test(value);
}

/**
 * Parse event date strings correctly, handling both date-only (YYYY-MM-DD)
 * and ISO datetime strings. Date-only strings are parsed as LOCAL midnight
 * to avoid UTC timezone shifts.
 */
export function parseEventDate(dateStr: unknown): Date | null {
  if (typeof dateStr !== 'string' || !dateStr) return null;

  // Date-only: parse as LOCAL midnight (floating date)
  if (isDateOnlyString(dateStr)) {
    const [y, m, d] = dateStr.split('-').map(Number);
    return new Date(y, m - 1, d, 0, 0, 0, 0);
  }

  const dt = new Date(dateStr);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/**
 * Get the start of a local day (midnight)
 */
export function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

/**
 * Check if a date is today (LOCAL year/month/day equality).
 */
export function isToday(date: Date): boolean {
  const today = new Date();
  return date.getDate() === today.getDate() &&
         date.getMonth() === today.getMonth() &&
         date.getFullYear() === today.getFullYear();
}

/**
 * Check if an event is all-day based on either the allDay flag
 * or the date format of startAt
 */
export function isAllDayish(event: { allDay?: boolean; startAt: string }): boolean {
  return Boolean(event.allDay) || isDateOnlyString(event.startAt);
}

/** Format HH:MM to display label like "5:56 PM" */
export function formatTimeLabel(value: string): string {
  if (!value) return '';
  const match = value.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return value;
  const h = parseInt(match[1], 10);
  const m = match[2];
  const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const ampm = h < 12 ? 'AM' : 'PM';
  return `${hour12}:${m} ${ampm}`;
}

/** Parse typed text like "5:56pm", "17:56", "530p", "5pm" into HH:MM */
export function parseTimeInput(raw: string): string | null {
  const s = raw.trim().toLowerCase().replace(/\s+/g, '');
  if (!s) return null;

  // Try HH:MM (24h)
  let m = s.match(/^(\d{1,2}):(\d{2})$/);
  if (m) {
    const h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    if (h >= 0 && h < 24 && min >= 0 && min < 60) {
      return `${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
    }
  }

  // Try H:MMam/pm or HH:MMam/pm
  m = s.match(/^(\d{1,2}):(\d{2})(a|am|p|pm)$/);
  if (m) {
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const isPm = m[3].startsWith('p');
    if (h === 12) h = isPm ? 12 : 0;
    else if (isPm) h += 12;
    if (h >= 0 && h < 24 && min >= 0 && min < 60) {
      return `${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
    }
  }

  // TryHam/pm or HHam/pm (no minutes)
  m = s.match(/^(\d{1,2})(a|am|p|pm)$/);
  if (m) {
    let h = parseInt(m[1], 10);
    const isPm = m[2].startsWith('p');
    if (h === 12) h = isPm ? 12 : 0;
    else if (isPm) h += 12;
    if (h >= 0 && h < 24) {
      return `${h.toString().padStart(2, '0')}:00`;
    }
  }

  // Try HMM or HHMM (no colon, like "530" or "1730")
  m = s.match(/^(\d{1,2})(\d{2})(a|am|p|pm)?$/);
  if (m) {
    let h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    const ampm = m[3];
    if (ampm) {
      const isPm = ampm.startsWith('p');
      if (h === 12) h = isPm ? 12 : 0;
      else if (isPm) h += 12;
    }
    if (h >= 0 && h < 24 && min >= 0 && min < 60) {
      return `${h.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
    }
  }

  return null;
}

/**
 * Format an existing event's start/end ISO timestamp into an `HH:MM` 24h string
 * for the edit-modal time inputs, using the LOCAL time-of-day.
 *
 * This must show the event's REAL minutes. It previously rounded to the nearest
 * 15 (`Math.round(min / 15) * 15`), so an event at 11:50 displayed — and, since
 * the modal saves straight from the input, PERSISTED — as 11:45 (#4287).
 * New-event defaults still snap to the next quarter-hour via `roundToNearest15`
 * in EventEditModal; only seeding an existing event's time must be exact.
 */
export function formatTimeForInput(isoString: string): string {
  if (!isoString) return '';
  const d = new Date(isoString);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
